<template>
  <div class="pablo-chat-wrapper" :class="{ 'is-open': isOpen }">
    <!-- Floating toggle button -->
    <button class="pablo-chat-toggle" @click="toggle">
      💬 Ask Pablo
    </button>

    <!-- Slide-up / side panel -->
    <div class="pablo-chat-panel">
      <div class="pablo-chat-header">
        <div>
          <h3>Pablo – Production Analyst</h3>
          <p class="pablo-chat-subtitle">
            Location: {{ loc }} • Window: {{ planningDays }} day<span v-if="planningDays !== 1">s</span>
          </p>
        </div>
        <button class="pablo-close-btn" @click="isOpen = false">✕</button>
      </div>

      <div class="pablo-chat-body" ref="scrollContainer">
        <div
          v-for="(m, idx) in messages"
          :key="idx"
          class="chat-message"
          :class="m.role"
        >
          <div class="chat-badge" v-if="m.role === 'assistant'">Pablo</div>
          <div class="chat-badge" v-else>You</div>
          <div class="chat-bubble">{{ m.content }}</div>
        </div>

        <div v-if="loading" class="chat-loading">
          Pablo is thinking…
        </div>
      </div>

      <form class="pablo-chat-input" @submit.prevent="sendMessage">
        <textarea
          v-model="input"
          placeholder="Ask Pablo about your schedule, capacity, shortages..."
          rows="2"
        ></textarea>
        <button type="submit" :disabled="!input.trim() || loading">
          Send
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PabloChatSidebar',
  props: {
    loc: {
      type: String,
      required: true,
    },
    planningDays: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      isOpen: false,
      messages: [
        {
          role: 'assistant',
          content:
            'Hi, I’m Pablo. Ask me anything about your current schedule, machine load, or shortages.',
        },
      ],
      input: '',
      loading: false,
    };
  },

  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$nextTick(this.scrollToBottom);
      }
    },

    scrollToBottom() {
      const el = this.$refs.scrollContainer;
      if (el) el.scrollTop = el.scrollHeight;
    },

    async sendMessage() {
      const text = this.input.trim();
      if (!text || this.loading) return;

      // Add user's message
      this.messages.push({ role: 'user', content: text });
      this.input = '';
      this.loading = true;
      this.$nextTick(this.scrollToBottom);

      try {
        // 🔥 JPL Pablo chat endpoint (matches server-jpl.js)
        const res = await axios.post('/pablo-chat', {
          loc: this.loc,
          planningDays: this.planningDays,
          messages: this.messages.map(m => ({
            role: m.role,
            content: m.content,
          })),
        });

        const reply =
          res.data?.reply ||
          'I had trouble answering that. Please try again later.';

        this.messages.push({
          role: 'assistant',
          content: reply,
        });
      } catch (err) {
        console.error('Pablo chat error:', err);

        this.messages.push({
          role: 'assistant',
          content:
            'I ran into an error talking to the Pablo service. Please try again in a moment.',
        });
      } finally {
        this.loading = false;
        this.$nextTick(this.scrollToBottom);
      }
    },
  },
};
</script>

<style scoped>
.pablo-chat-wrapper {
  position: fixed;
  bottom: 1rem;
  left: 1rem;
  z-index: 9999;
}

/* Toggle button */
.pablo-chat-toggle {
  border-radius: 999px;
  padding: 0.5rem 1rem;
  border: none;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  cursor: pointer;
  background: #5595b7c2;
  color: #fff;
  font-weight: 500;
}

/* Panel container */
.pablo-chat-panel {
  position: fixed;
  bottom: 3.5rem;
  left: 1rem;
  width: 360px;
  max-height: 70vh;
  background: #0e0b0b;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(0,0,0,0.2);
  display: none;
  flex-direction: column;
  color: #f9fafb;
}

.pablo-chat-wrapper.is-open .pablo-chat-panel {
  display: flex;
}

.pablo-chat-header {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #f9fafb;
}

.pablo-chat-header h3 {
  margin: 0;
  font-size: 1rem;
}

.pablo-chat-subtitle {
  margin: 0;
  font-size: 0.75rem;
  color: #d1d5db;

}

.pablo-close-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1rem;
  color: #f9fafb;
}

.pablo-chat-body {
  padding: 0.75rem;
  overflow-y: auto;
  flex: 1;
}

.chat-message {
  margin-bottom: 0.75rem;
  display: flex;
  flex-direction: column;
}

.chat-message.user {
  align-items: flex-end;
}

.chat-message.assistant {
  align-items: flex-start;
}

.chat-badge {
  font-size: 0.65rem;
  opacity: 0.7;
  margin-bottom: 0.15rem;
}

.chat-bubble {
  padding: 0.4rem 0.6rem;
  border-radius: 8px;
  max-width: 90%;
  white-space: pre-wrap;
  color: #f9fafb;
}

.chat-message.user .chat-bubble {
  background: #141d24;
}

.chat-message.assistant .chat-bubble {
  background: #091e4b;
}

.chat-loading {
  font-size: 0.8rem;
  color: #666;
}

.pablo-chat-input {
  border-top: 1px solid #eee;
  padding: 0.5rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.pablo-chat-input textarea {
  width: 100%;
  resize: vertical;
  background: #111827;         /* 👈 dark input bg */
  color: #f9fafb;              /* 👈 light input text */
  border-radius: 6px;
  border: 1px solid #374151;
}

.pablo-chat-input button {
  align-self: flex-end;
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  background: #1976d2;
  color: #fff;
}
</style>
